import React from 'react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { ModalPortalTarget } from '@jarvis.oddle/core/components/Modal'
import { ToastContainer } from '@jarvis.oddle/core/components/Toast'
import { TranslationProvider } from '@jarvis.oddle/core/components/Translation'
import {
  API_ROOT as apiRoot,
  origin,
  ROLLBAR_ACCESS_TOKEN_CLIENT
} from 'config/client'
import FontContainer from 'containers/FontContainer'
import Error, { errorTypes } from '@jarvis.oddle/core/components/Error'
import { margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import { getUrlObject } from '@jarvis.oddle/core/tools/routes'

// new
import ConnectedErrorLogger from './ConnectedErrorLogger'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectPreviewMode } from 'src/redux/customisation'
import { LayoutProps } from 'src/utils/props'
import useTracker from 'src/hooks/useTracker'
import { ResponsiveContextProvider } from 'src/decorators/Responsive'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { clearCacheMenuAction } from 'src/redux/meta'
import FrameConnector from 'src/containers/FrameConnector'
import ThemeProvider from 'src/containers/ThemeProvider'
import AppContextProvider from 'src/containers/AppContext'
import ExternalScripts from 'src/containers/ExternalScripts'
import { StoreOrderProvider } from 'src/containers/StoreOrder'
import { StoreItemAvailabilityProvider } from 'src/containers/StoreItemAvailability'
import PageHead from 'src/containers/PageHead'
import Microtag from 'src/containers/Microtag'
import PerformanceTracker from 'src/containers/PerformanceTracker'
import { UserContextProvider } from 'src/containers/UserContext'
import ShopPageProgress from 'src/components/molecules/ShopPageProgress'
import { OrderingFlowContextProvider } from 'src/containers/OrderingFlowContext'
import AnnouncementBar from 'src/components/molecules/AnnouncementBar'

const UntypedMicroTag = Microtag as ({ currentUrl: string }) => JSX.Element
const UntypedFrameConnector = FrameConnector as ({
  children: ReactNode,
  enabled: boolean
}) => JSX.Element
const Modals = dynamic(() => import('src/containers/Modals'))

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const BodyWrapper = styled.div`
  flex-grow: 1;
  background-color: ${(props) => props.theme.colorBackgroundColor};
`

const StyledShopLink = styled.a`
  margin-top: ${margin[marginSizes.medium]};
`

const Layout = ({
  children,
  currentUrl,
  appVersion,
  translation,
  locale,
  showTranslationKey,
  error,
  userAgent
}: LayoutProps): JSX.Element => {
  const previewMode = useAppSelector(selectPreviewMode)
  const [currentRouter, setCurrentRouter] = React.useState(null)
  const { notifyPageView } = useTracker()
  const dispatch = useAppDispatch()

  const url = getUrlObject()

  React.useEffect(() => {
    if (JSON.stringify(url.query) !== currentRouter) {
      notifyPageView()
      setCurrentRouter(JSON.stringify(url.query))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRouter, url.query])

  const getErrorType = (error) => {
    if (error) {
      const omsError = Array.isArray(error) ? error[0] : error
      switch (omsError.errorCode) {
        case 'MenuNotFound':
        case 'SiteNotFound':
          return errorTypes.menuNotFound
        case 'StoreDeleted':
          return errorTypes.storeDeleted
        case 'StoreNotEnablePickupDelivery':
          return errorTypes.storeNotEnablePickupDelivery
        case 'StoreNoPaymentOptionPickup':
          return errorTypes.storeNoPaymentOptionPickup
        case 'StoreNoTimeOption':
          return errorTypes.storeNoTimeOption
        case 'StoreNotEnablePickup':
          return errorTypes.storeNotEnablePickup
        case 'StoreLocationNotFound':
          return errorTypes.storeLocationNotFound
        case 'StoreNoPaymentOptionDelivery':
          return errorTypes.storeNoPaymentOptionDelivery
        case 'StoreNotEnableDelivery':
          return errorTypes.storeNotEnableDelivery
        case 'StoreNotOpen':
          return errorTypes.storeNotOpen
        case 'MenuNotAvailable':
          return errorTypes.menuNotAvailable
        case 'TemporarilyUnavailable':
          return errorTypes.temporarilyUnavailable
        case 'AccountSuspended':
          return errorTypes.accountSuspended
        case 'MenuIsEmpty':
          return errorTypes.menuIsEmpty
        case '404':
          return errorTypes['404']
        case '500':
          return errorTypes['500']
        default:
          return omsError.errorCode
      }
    }
    return null
  }

  const handleClickReset = async () => {
    await dispatch(clearCacheMenuAction())
  }

  return (
    <TranslationProvider
      translation={translation}
      locale={locale}
      origin={origin}
      apiRoot={apiRoot}
      showTranslationKey={showTranslationKey}
    >
      <AppContextProvider>
        <ShopPageProgress />
        <ConnectedErrorLogger
          appVersion={appVersion}
          rollbarAccessToken={ROLLBAR_ACCESS_TOKEN_CLIENT}
        />
        <PerformanceTracker />
        <ThemeProvider>
          <StoreOrderProvider>
            <StoreItemAvailabilityProvider>
              <UserContextProvider>
                <OrderingFlowContextProvider>
                  <ResponsiveContextProvider userAgent={userAgent}>
                    <FontContainer />
                    {error ? (
                      <Error
                        currentUrl={currentUrl}
                        type={getErrorType(error)}
                        locale={locale}
                        LinkComponent={StyledShopLink}
                        ctaLink={
                          getErrorType(error) === errorTypes.storeClosed
                            ? error.ctaLink
                            : null
                        }
                        onClickReset={handleClickReset}
                        errorDetails={error}
                      />
                    ) : (
                      <React.Fragment>
                        <PageHead currentUrl={currentUrl} />
                        <UntypedMicroTag currentUrl={currentUrl} />
                        <PageWrapper>
                          <UntypedFrameConnector enabled={previewMode}>
                            <AnnouncementBar />
                            <BodyWrapper>{children}</BodyWrapper>
                          </UntypedFrameConnector>
                        </PageWrapper>
                        <Modals />
                        <ToastContainer />
                        <ExternalScripts />
                        <ModalPortalTarget />
                      </React.Fragment>
                    )}
                  </ResponsiveContextProvider>
                </OrderingFlowContextProvider>
              </UserContextProvider>
            </StoreItemAvailabilityProvider>
          </StoreOrderProvider>
        </ThemeProvider>
      </AppContextProvider>
    </TranslationProvider>
  )
}

export default Layout
